/**
 * Integration Translation Helpers
 * 
 * This file provides helper functions to access translated text from integration configurations.
 * It bridges the gap between the key-based config system and components that need the actual text.
 */

import { IntegrationConfig, IntegrationSourceType, IntegrationField } from './integrations';

export interface TranslatedIntegrationConfig extends Omit<IntegrationConfig, 'nameKey' | 'descriptionKey' | 'sourceTypes'> {
  name: string;
  description: string;
  sourceTypes: TranslatedIntegrationSourceType[];
}

export interface TranslatedIntegrationSourceType extends Omit<IntegrationSourceType, 'nameKey' | 'descriptionKey' | 'fields' | 'helpContent'> {
  name: string;
  description: string;
  fields: TranslatedIntegrationField[];
  helpContent: {
    title: string;
    items: string[];
    examples?: { title: string; description: string }[];
  };
}

export interface TranslatedIntegrationField extends Omit<IntegrationField, 'labelKey' | 'placeholderKey' | 'descriptionKey'> {
  label: string;
  placeholder?: string;
  description?: string;
}

// Type for the translation function - simplified to match actual usage
type TranslationFunction = (key: string, defaultValue?: string) => string;

/**
 * Translates an integration config by resolving all translation keys
 */
export function translateIntegrationConfig(
  config: IntegrationConfig,
  t: TranslationFunction
): TranslatedIntegrationConfig {
  return {
    ...config,
    name: t(config.nameKey, config.nameKey),
    description: t(config.descriptionKey, config.descriptionKey),
    sourceTypes: config.sourceTypes.map(st => translateSourceType(st, t))
  };
}

/**
 * Translates a source type config by resolving all translation keys
 */
export function translateSourceType(
  sourceType: IntegrationSourceType,
  t: TranslationFunction
): TranslatedIntegrationSourceType {
  return {
    ...sourceType,
    name: t(sourceType.nameKey, sourceType.nameKey),
    description: t(sourceType.descriptionKey, sourceType.descriptionKey),
    fields: sourceType.fields.map(f => translateField(f, t)),
    helpContent: {
      title: t(sourceType.helpContent.titleKey, sourceType.helpContent.titleKey),
      items: sourceType.helpContent.itemsKeys.map(key => t(key, key)),
      examples: sourceType.helpContent.examples?.map(ex => ({
        title: t(ex.titleKey, ex.titleKey),
        description: t(ex.descriptionKey, ex.descriptionKey)
      }))
    }
  };
}

/**
 * Translates a field config by resolving all translation keys
 */
export function translateField(
  field: IntegrationField,
  t: TranslationFunction
): TranslatedIntegrationField {
  return {
    ...field,
    label: t(field.labelKey, field.labelKey),
    placeholder: field.placeholderKey ? t(field.placeholderKey, field.placeholderKey) : undefined,
    description: field.descriptionKey ? t(field.descriptionKey, field.descriptionKey) : undefined
  };
}
